import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import WithRender from './NewUpdateRequest.html';
import { Inject } from 'vue-di-container';
import { v4 as uuidv4 } from 'uuid';
import FileSaver from 'file-saver';
import ValidationService from '../../../../Services/ValidationService';
import { Toaster } from '../../../../Services/toast';
import { SenderType } from '../../../../shared/Enums/SenderType';
import { DropdownModel, ProviderModel } from '../../../Provider/ProviderModel';
import { AttachmentCategoryType } from '../../../../shared/Enums/AttachmentCategory';
import NewUpdateRequestService from './NewUpdateRequestService';
import ProviderService from '../../../Provider/ProviderService';
import UserService from '../../../../shared/userService/UserService';
import VirtualOperatorRules from '@/shared/VirtualOperatorRules';
import {
    AddUpdateRequestModel,
    PaidSenderForUpdateRequestModel,
    UpdateRequestLookupResult,
} from './NewUpdateRequestModels';

@WithRender
@Component({
    components: {
    },
    diProvide: [
        NewUpdateRequestService,
        ProviderService,
        UserService,
    ],
})
export default class NewUpdateRequest extends Vue {
    @Inject(ValidationService) public validationService!: ValidationService;
    @Inject(NewUpdateRequestService) public newRequestService!: NewUpdateRequestService;
    @Inject(ProviderService) public providerService!: ProviderService;
    @Inject(UserService) public userService!: UserService;

    @Prop({ type: Boolean, default: false })
    public layoutSup: boolean | any;

    public showEntityInfo: boolean = false;
    public editDialog: boolean = false;
    public editStep: number = 1;
    public saveInFlight: boolean = false;
    public selectedFileName: string = '';
    public existingDelegationLetterAttachmentId: string = '';
    public existingDelegationLetterFileName: string = '';
    public referenceError: string = '';

    public lookupModel = {
        customerType: 0,
        crNumber: '',
        enterpriseUnifiedNumber: '',
    };

    public entityInfo: UpdateRequestLookupResult = {
        clientName: '',
        crNumber: '',
        enterpriseUnifiedNumber: '',
        customerType: 0,
        senders: [],
    };

    public senders: PaidSenderForUpdateRequestModel[] = [];

    public editModel: AddUpdateRequestModel = {
        senderId: '',
        updateType: 1,
        representativeName: '',
        representativeMobile: '',
        representativeEmail: '',
        providerComment: '',
        attachments: [],
    };

    public originalEditValues = {
        representativeName: '',
        representativeMobile: '',
        representativeEmail: '',
    };

    public headers: any = [
        { text: this.$t('shared.senderName'), value: 'senderName', align: 'center', sortable: false },
        { text: this.$t('shared.senderType'), value: 'senderType', align: 'center', sortable: false },
        { text: this.$t('shared.actions'), value: 'actions', align: 'center', sortable: false },
    ];

    public customerTypeList: DropdownModel[] = [
        { id: 1, name: this.$t('shared.government').toString() },
        { id: 2, name: this.$t('shared.charity').toString() },
        { id: 3, name: this.$t('shared.private').toString() },
        { id: 4, name: this.$t('shared.international').toString() },
    ];

    public provider: ProviderModel = {
        id: '',
        providerCode: '',
        name: '',
        createdDate: null,
        enterpriseUnifiedNumber: '',
        accreditationNumber: '',
        email: '',
        taxNumber: '',
        hasInternational: false,
        country: '',
        city: '',
        district: '',
        streetName: '',
        buildingNumber: '',
        postalCode: '',
        vatNumber: '',
        additionalNumber: '',
        otherIdentifier: '',
        creditLimitAmount: 0,
        balance: 0,
        bankAccountNumber: '',
        expireDate: '',
        state: '',
        isDisconnected: '',
        integrationBaseURL: '',
        isVirtualOperator: false,
        virtualOperatorId: null,
        virtualOperatorName: '',
    };

    public async mounted() {
        this.$emit('childinit', this.layoutSup);
        const user = await this.userService.getUser();
        if (user && user.details && user.details.ProviderId) {
            this.provider = await this.providerService.GetById(user.details.ProviderId);
            if (!this.provider.hasInternational) {
                this.customerTypeList.splice(3, 1);
            }
            VirtualOperatorRules.applyPrivateClientFields(this.lookupModel, this.provider, false);
        }
    }

    @Watch('lookupModel.customerType')
    public onCustomerTypeChanged() {
        if (this.provider.isVirtualOperator) {
            this.lookupModel.customerType = 3;
        }
        this.showEntityInfo = false;
        this.senders = [];
        this.entityInfo = {
            clientName: '',
            crNumber: '',
            enterpriseUnifiedNumber: '',
            customerType: 0,
            senders: [],
        };
        this.lookupModel.crNumber = '';
        this.lookupModel.enterpriseUnifiedNumber = '';
        VirtualOperatorRules.applyPrivateClientFields(this.lookupModel, this.provider, false);
        this.referenceError = '';
    }

    public get isValidApply(): boolean {
        const needsCr = this.lookupModel.customerType !== 3;
        const hasCr = this.lookupModel.crNumber.trim() !== '';
        const unifiedValid =
            (this.lookupModel.customerType === 2 || this.lookupModel.customerType === 3)
                ? this.lookupModel.enterpriseUnifiedNumber.trim() !== ''
                : true;
        return (
            this.lookupModel.customerType !== 0 &&
            (!needsCr || hasCr) &&
            unifiedValid
        );
    }

    public get hasDelegationLetter(): boolean {
        return this.editModel.attachments.length > 0 ||
            !!this.existingDelegationLetterAttachmentId;
    }

    public get displayedFileName(): string {
        return this.selectedFileName || this.existingDelegationLetterFileName || '';
    }

    public get isEditValid(): boolean {
        return (
            this.editModel.representativeName.trim() !== '' &&
            this.editModel.representativeMobile.trim() !== '' &&
            this.editModel.representativeMobile.length === 9 &&
            this.editModel.representativeMobile.startsWith('5') &&
            this.editModel.representativeEmail.trim() !== '' &&
            this.validationService.isValidEmail(this.editModel.representativeEmail) &&
            this.hasDelegationLetter
        );
    }

    public get hasEditChanges(): boolean {
        const nameChanged =
            this.editModel.representativeName.trim() !== this.originalEditValues.representativeName.trim();
        const mobileChanged =
            this.editModel.representativeMobile.trim() !== this.originalEditValues.representativeMobile.trim();
        const emailChanged =
            this.editModel.representativeEmail.trim().toLowerCase() !==
            this.originalEditValues.representativeEmail.trim().toLowerCase();
        const letterChanged = this.editModel.attachments.length > 0;
        return nameChanged || mobileChanged || emailChanged || letterChanged;
    }

    public get representativeMobileError(): string {
        if (this.editModel.representativeMobile &&
            !this.editModel.representativeMobile.startsWith('5')) {
            return this.$t('shared.NumberStart5').toString();
        }
        return '';
    }

    public get representativeEmailError(): string {
        if (this.editModel.representativeEmail &&
            !this.validationService.isValidEmail(this.editModel.representativeEmail)) {
            return this.$t('shared.InvalidEmail').toString();
        }
        return '';
    }

    public get referenceLabel(): string {
        switch (this.lookupModel.customerType) {
            case 1:
                return this.$t('shared.budgetNumber').toString();
            case 2:
                return this.$t('shared.licenseNumber').toString();
            case 3:
                return this.$t('shared.enterpriseUnifiedNumber').toString();
            case 4:
                return this.$t('shared.internationalCommercialRegistrationNumber').toString();
            default:
                return this.$t('shared.enterpriseUnifiedNumber').toString();
        }
    }

    public async apply() {
        this.referenceError = '';
        if (!this.isValidApply) {
            this.referenceError = this.$t('shared.thisFieldIsRequired').toString();
            Toaster.error(this.referenceError);
            return;
        }

        const response = await this.newRequestService.LookupPaidSenders(
            this.lookupModel.customerType,
            this.lookupModel.crNumber,
            this.lookupModel.enterpriseUnifiedNumber,
        );

        if (!response || !response.isSuccess) {
            const message = (response && response.errorMessage)
                ? response.errorMessage
                : this.$t('shared.noDataFound').toString();
            this.referenceError = message;
            Toaster.error(message);
            this.showEntityInfo = false;
            this.senders = [];
            return;
        }

        this.entityInfo = response.result;
        this.senders = (response.result.senders || []).map((s: any) => {
            const item: PaidSenderForUpdateRequestModel = Object.assign({}, s);
            item.senderId = s.senderId || s.SenderId || '';
            item.originalRequestId = s.originalRequestId || s.OriginalRequestId || '';
            item.senderName = s.senderName || s.SenderName || '';
            item.senderType = s.senderType != null ? s.senderType : s.SenderType;
            item.representativeName = s.representativeName || s.RepresentativeName || '';
            item.representativeMobile = s.representativeMobile || s.RepresentativeMobile || '';
            item.representativeEmail = s.representativeEmail || s.RepresentativeEmail || '';
            item.delegationLetterAttachmentId = s.delegationLetterAttachmentId || s.DelegationLetterAttachmentId || '';
            item.delegationLetterFileName = s.delegationLetterFileName || s.DelegationLetterFileName || '';
            item.senderTypes = [];
            this.fillSenderTypes(item.senderType, item);
            return item;
        });
        this.showEntityInfo = true;
        this.referenceError = '';
    }

    public fillSenderTypes(type: number, sender: PaidSenderForUpdateRequestModel) {
        sender.senderTypes = [];
        if ((type & 1) === 1) {
            sender.senderTypes.push(1);
        }
        if ((type & 2) === 2) {
            sender.senderTypes.push(2);
        }
        if ((type & 4) === 4) {
            sender.senderTypes.push(4);
        }
        if ((type & 8) === 8) {
            sender.senderTypes.push(8);
        }
    }

    public getSenderTypeText(typeId: number) {
        switch (typeId) {
            case SenderType.Advertisement:
                return this.$t('shared.advertisement').toString();
            case SenderType.Service:
                return this.$t('shared.service').toString();
            case SenderType.Awareness:
                return this.$t('shared.awareness').toString();
            case SenderType.Warning:
                return this.$t('shared.warning').toString();
            default:
                return '-';
        }
    }

    public openEdit(sender: PaidSenderForUpdateRequestModel) {
        this.editModel = {
            senderId: sender.senderId,
            updateType: 1,
            representativeName: sender.representativeName || '',
            representativeMobile: sender.representativeMobile || '',
            representativeEmail: sender.representativeEmail || '',
            providerComment: '',
            attachments: [],
        };
        this.originalEditValues = {
            representativeName: this.editModel.representativeName,
            representativeMobile: this.editModel.representativeMobile,
            representativeEmail: this.editModel.representativeEmail,
        };
        this.selectedFileName = '';
        this.existingDelegationLetterAttachmentId = sender.delegationLetterAttachmentId
            ? String(sender.delegationLetterAttachmentId)
            : '';
        this.existingDelegationLetterFileName = sender.delegationLetterFileName || '';
        this.editStep = 1;
        this.editDialog = true;
    }

    public cancelEdit() {
        this.editModel.representativeName = this.originalEditValues.representativeName;
        this.editModel.representativeMobile = this.originalEditValues.representativeMobile;
        this.editModel.representativeEmail = this.originalEditValues.representativeEmail;
        this.editModel.attachments = [];
        this.editModel.providerComment = '';
        this.selectedFileName = '';
        this.existingDelegationLetterAttachmentId = '';
        this.existingDelegationLetterFileName = '';
        this.editStep = 1;
        this.editDialog = false;
    }

    public goToEditReview() {
        if (!this.isEditValid) {
            if (this.representativeEmailError) {
                Toaster.error(this.representativeEmailError);
            } else if (this.representativeMobileError ||
                (this.editModel.representativeMobile.trim() !== '' &&
                    this.editModel.representativeMobile.length !== 9)) {
                Toaster.error(this.$t('shared.InvalidMobileNumber').toString());
            } else {
                Toaster.error(this.$t('shared.thisFieldIsRequired').toString());
            }
            return;
        }
        if (!this.hasEditChanges) {
            Toaster.error(this.$t('updateDataRequestsPage.nothingChanged').toString());
            return;
        }
        this.editStep = 2;
    }

    public backToEditStep() {
        this.editStep = 1;
    }

    public async downloadExistingLetter() {
        if (!this.existingDelegationLetterAttachmentId) {
            return;
        }
        const fileContent = await this.newRequestService.GetSenderRequestFileContent(
            this.existingDelegationLetterAttachmentId);
        if (!fileContent) {
            Toaster.error(this.$t('shared.errorOccurred').toString());
            return;
        }
        const array = Buffer.from(fileContent, 'base64');
        const blob = new Blob([array], { type: 'application/pdf' });
        const fileName = this.existingDelegationLetterFileName
            ? this.existingDelegationLetterFileName
            : 'DelegationLetter.pdf';
        FileSaver.saveAs(blob, fileName);
    }

    public onPickFile() {
        (this.$refs.delegationFile as any).click();
    }

    public onFilePicked(event: any) {
        const files: any[] = event.target.files;
        if (!files || !files.length) {
            return;
        }
        const file = files[0];
        if (file.size > 3000000) {
            Toaster.error(this.$t('shared.fileSizeExceedsTheLimit').toString());
            return;
        }
        this.selectedFileName = file.name;
        const reader = new FileReader();
        reader.onloadend = (fileData: any) => {
            this.editModel.attachments = [{
                id: uuidv4(),
                fileName: file.name,
                fileContent: fileData.target.result.split(',')[1],
                attachmentType: AttachmentCategoryType.DelegationLetter,
                attachmentTypeName: 'Delegation Letter',
                isDeleted: false,
            }];
        };
        reader.readAsDataURL(file);
        if (this.$refs.delegationFile) {
            (this.$refs.delegationFile as any).value = '';
        }
    }

    public isNumberField(val: KeyboardEvent) {
        if (!this.validationService.isNumber(val.key)) {
            val.preventDefault();
        }
    }

    public isNumberFieldPaste(ev: any) {
        const paste = (ev.clipboardData).getData('text');
        if (isNaN(paste)) {
            ev.preventDefault();
        }
    }

    public isMobileNumberValid() {
        if (this.editModel.representativeMobile &&
            (!this.editModel.representativeMobile.startsWith('5') ||
                this.editModel.representativeMobile.length !== 9)) {
            Toaster.error(this.$t('shared.InvalidMobileNumber').toString());
        }
    }

    public async saveEdit() {
        if (!this.isEditValid || this.saveInFlight) {
            if (this.representativeEmailError) {
                Toaster.error(this.representativeEmailError);
            } else if (this.representativeMobileError ||
                (this.editModel.representativeMobile.trim() !== '' &&
                    this.editModel.representativeMobile.length !== 9)) {
                Toaster.error(this.$t('shared.InvalidMobileNumber').toString());
            } else if (this.editModel.representativeName.trim() === '' ||
                this.editModel.representativeMobile.trim() === '' ||
                this.editModel.representativeEmail.trim() === '' ||
                !this.hasDelegationLetter) {
                Toaster.error(this.$t('shared.thisFieldIsRequired').toString());
            }
            return;
        }
        this.saveInFlight = true;
        try {
            const response = await this.newRequestService.AddRequest(this.editModel);
            if (!response || !response.isSuccess) {
                Toaster.error(
                    (response && response.errorMessage) || this.$t('shared.errorOccurred').toString(),
                );
                return;
            }
            Toaster.success(this.$t('shared.updateRequestCreatedSuccessfully').toString());
            this.editDialog = false;
            this.editStep = 1;
            this.selectedFileName = '';
            this.existingDelegationLetterAttachmentId = '';
            this.existingDelegationLetterFileName = '';
            await this.apply();
        } finally {
            this.saveInFlight = false;
        }
    }

    public cancel() {
        this.$router.push({ name: 'UpdateRequests' });
    }
}
